import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Autocomplete from "./Autocomplete";
import CacheMap from "../../lib/form/CacheMap";
import pDebounce from "p-debounce";
import { remove } from "remove-accents";

const cache = new CacheMap("fetch_r1e_city");

const fetchData = () => {
  return pDebounce(async (inputValue, value, setOptions, setLoading) => {
    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    const query = encodeURIComponent(remove(inputValue).toLowerCase());
    const url = `https://cc-acquisition.reserverunessai.com/ws/get/cities/${query}`;

    setLoading(true);

    let result;
    if (cache.has(url)) {
      result = cache.get(url);
    } else {
      const response = await fetch(
        url,
        {
          method: "GET",
          headers: {},
          mode: "cors",
          cache: "default",
        }
      );
      result = await response.json();
      cache.set(url, result);
    }

    setLoading(false);

    if (result && Array.isArray(result)) {
      setOptions(result.map(city => ({
        ...city,
        id: city.geo_id,
        label: `${city.nom}, ${city.code_postal} `,
        value: city.code_postal
      })));
    }
  }, 500);
};

function R1ECity({
  methods,
  label,
  name,
  codePostal,
  idLocalite,
  required,
  variant,
}) {
  const { setValue, register } = methods;

  return (
    <React.Fragment>
      <Autocomplete
        name={name}
        label={label}
        required={required}
        variant={variant}
        methods={methods}
        onChange={(event, newValue) => {
          setValue(codePostal, newValue?.code_postal);
          setValue(idLocalite, newValue?.idlocalite);
        }}
        renderOption={(props, option) => (
          <li {...props} key={`li_city_${option.id}${props.key}`}>
            <Box key={`box_city_${option.geo_id}${props.key}`} m={0}>
              <span
                key={`city_${option.geo_id}${props.key}`}
                style={{ fontWeight: 400 }}
              >
                {option.nom}
              </span>
              , {option.code_postal}
              <Typography
                key={`typo_city_${option.geo_id}${props.key}`}
                variant="body2"
                color="textSecondary"
              >
                {option.context}
              </Typography>
            </Box>
          </li>
        )}
        dataLoader={fetchData()}
      />

      <input {...register(codePostal)} type="hidden" />
      <input {...register(idLocalite)} type="hidden" />
    </React.Fragment>
  );
}

R1ECity.defaultProps = {
  label: "Votre ville",
  name: "autocompletion_city",
  cityName: "city",
  codePostal: "zipcode",
  idLocalite: "idlocalite",
  required: "Merci de saisir votre ville",
  variant: "outlined",
};

R1ECity.propTypes = {
  /**
   * methods du useForm()
   */
  methods: PropTypes.instanceOf(Object),
  /**
   * Label du champ
   */
  label: PropTypes.string,
  /**
   * Name du tag HTML input
   */
  name: PropTypes.string,
  /**
   * Name du tag HTML input contenant le nom de la ville
   */
  cityName: PropTypes.string,
  /**
   * Name du tag HTML input contenant le code postal
   */
  codePostal: PropTypes.string,
  /**
   * Name du tag HTML input contenant le code insee de la commune
   */
  idLocalite: PropTypes.string,
  /**
   * Message d'erreur si le champ est requis
   */
  required: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /**
   * The variant to use
   * 'filled'
   * | 'outlined'
   * | 'standard'
   */
  variant: PropTypes.string,
};

export default R1ECity;
